/** Entry point for the LabelMap. */

import { LabelDetail } from '../common/label-detail/LabelDetail.js';
import { LabelPopup } from '../common/label-detail/LabelPopup.js';
import { viewerClassFor } from '../common/pano-viewer/viewerClassFor.js';
import { initLabelMapLocationSearch } from '../labelMapLocationSearch.js';
import { MapDownloadControl } from '../ps-map/MapDownloadControl.js';
import { MapLoadingOverlay } from '../ps-map/MapLoadingOverlay.js';
import { MapSidebarFilter } from '../ps-map/MapSidebarFilter.js';
import { MapSidebarUrlSync } from '../ps-map/MapSidebarUrlSync.js';
import { MapStatusPill } from '../ps-map/MapStatusPill.js';
import { createPSMap } from '../ps-map/createPSMap.js';
import { createLabelSpotlight } from '../ps-map/labelSpotlight.js';
import { createNearbyLabelNavigator } from '../ps-map/nearbyLabelNavigator.js';
import { setRegionFocus } from '../ps-map/psMapUtilities.js';
import '../../css/components/map-frame.css';

const data = document.getElementById('page-entry').dataset;
const imagerySrc = data.imagerySource;
const viewerType = viewerClassFor(imagerySrc);
const accessToken = data.imageryAccessToken;

// Shared with /admin/label-map so both label maps report loading and failure identically. Retry re-issues
// the viewport feed request in place; the reload fallback covers a failure before the loader exists.
const labelMapOverlay = new MapLoadingOverlay({
  onRetry: () => {
    const labelLoader = /** @type {any} */ (window).map?.mapData?.labelLoader;
    if (labelLoader) labelLoader.refetch();
    else window.location.reload();
  },
});

// Gets all translations before loading the choropleth.
window.appManager.ready(async () => {
  // Declared before params because the popup's callbacks below reference the helpers built from it, and a
  // ?labelId= deep link fires onShow while LabelPopup() is still being awaited — i.e. before this line
  // would otherwise run, which threw a temporal-dead-zone ReferenceError and aborted the deep-linked load.
  const self = {};

  // On this page the map is the open label's context (the spotlight beacon sits beside the dialog), so
  // swap the dialog's default dark backdrop for a light veil that keeps the spotlighted dot readable.
  const labelModal = /** @type {HTMLDialogElement} */ (document.getElementById('label-modal'));
  labelModal.classList.add('label-detail--map-host');

  const deepLinkLabelId = LabelDetail.urlLabelId();

  // label_id -> [lng, lat] of the label's pano and label_id -> label type, from the popup's metadata fetch.
  // Fallback data (the pano sits within ~15m of the label — under a beacon-width at zoom 16) for when the
  // map's own label data can't describe the label: before the large label layer has streamed in, or for
  // labels /labels/all doesn't carry.
  const cameraCoordsByLabelId = new Map();
  const labelTypeByLabelId = new Map();

  // Best known [lng, lat] / label type for a label: from the map data once loaded, else the popup metadata.
  const labelCoords = (labelId) =>
    (self.nearbyNav && self.nearbyNav.getCoords(labelId)) || cameraCoordsByLabelId.get(labelId) || null;
  const labelType = (labelId) =>
    (self.nearbyNav && self.nearbyNav.getLabelType(labelId)) || labelTypeByLabelId.get(labelId) || null;

  // Beacon + gutter camera + chat-bubble tail + sidebar-filter bypass for the popup's label — see
  // ps-map/labelSpotlight.js.
  const spotlight = createLabelSpotlight({
    dialog: labelModal,
    getMap: () => self.map,
    getMapData: () => self.mapData,
    getCoords: labelCoords,
    getLabelType: labelType,
  });

  const params = {
    mapName: 'labelmap-choropleth',
    mapStyle: 'mapbox://styles/mapbox/light-v11?optimize=true',
    mapboxApiKey: data.mapboxApiKey,
    mapboxLogoLocation: 'bottom-right',
    regionFillMode: 'singleColor',
    regionFillColor: '#808080',
    regionFillOpacity: 0.1,
    regionTooltip: 'none',
    differentiateUnauditedStreets: true,
    interactiveStreets: true,
    uiSource: 'LabelMap',
    // The view sends a username only for a real account: anonymous sessions have autogenerated usernames, and
    // cookie-less visitors none at all.
    popupLabelViewer: await LabelPopup(false, viewerType, accessToken, data.username || null, {
      syncUrlSource: 'LabelMap',
      showExploreHereLink: true,
      // Keep the shown label spotlighted beside the dialog while paging, and pulse it on close so the user
      // is re-anchored.
      onShow: (labelId) => spotlight.spotlight(labelId),
      onClose: (labelId) => spotlight.pulse(labelId),
      // A deep-linked label opens before (or without) the map's label data; once the popup's metadata
      // arrives, spotlight from its camera position rather than leaving the city-wide view up.
      onMetadata: (labelId, meta) => {
        if (meta.label_type) labelTypeByLabelId.set(labelId, meta.label_type);
        if (typeof meta.camera_lng !== 'number' || typeof meta.camera_lat !== 'number') return;
        cameraCoordsByLabelId.set(labelId, [meta.camera_lng, meta.camera_lat]);
        if (spotlight.spotlightedLabelId() !== labelId) spotlight.spotlight(labelId, true);
      },
      // A type edited from the card (#3671) moves the dot to its new type's layer right away.
      onEdit: (meta) => {
        if (labelType(meta.label_id) === meta.label_type) return;
        labelTypeByLabelId.set(meta.label_id, meta.label_type);
        self.mapData?.updateLabelType?.(meta.label_id, meta.label_type);
        // The navigator keeps its own label-to-type lookup, which the beacon reads its color from.
        self.nearbyNav?.refresh();
        spotlight.refreshBeacon(meta.label_id);
      },
      // The navigator isn't rebuilt: it would forget the open label, and Next needs a known label to page from.
      onDelete: (meta) => self.mapData?.setLabelDeleted?.(meta.label_id, meta.deleted),
    }),
    navigationControlPosition: 'top-right',
    highQualityFilter: true,
  };
  params.regionsURL = new URL('/regions', window.location.origin);
  params.completionRatesURL = new URL('/regions/completionRates', window.location.origin);
  params.streetsURL = new URL('/contribution/streets/all?filterLowQuality=true', window.location.origin);
  params.labelsURL = new URL('/labels/all', window.location.origin);

  const regionsParam = data.regionIds;
  if (regionsParam) {
    params.regionsURL.searchParams.append('regions', regionsParam);
    params.completionRatesURL.searchParams.append('regions', regionsParam);
    params.streetsURL.searchParams.append('regions', regionsParam);
    params.labelsURL.searchParams.append('regions', regionsParam);
  }

  const routesParam = data.routeIds;
  if (routesParam) {
    params.streetsURL.searchParams.append('routes', routesParam);
    params.labelsURL.searchParams.append('routes', routesParam);
  }

  const aiValidationOptionsParam = data.aiValidationOptions;
  if (aiValidationOptionsParam) {
    params.labelsURL.searchParams.append('aiValidationOptions', aiValidationOptionsParam);
  }

  // Labels load scoped to the viewport and refetch as the map moves (#5002); phones additionally open
  // zoomed in on the city center so the first fetch covers blocks, not the whole city, over cell data.
  params.viewportLabelLoading = true;
  params.mobileZoomedStart = true;

  // Mount the address search and reveal the loading indicator as soon as the map is ready, not after the (large)
  // label layer finishes loading, so they appear with the sidebar rather than popping in late (#4370/#4447).
  params.onMapReady = (map) => {
    self.map = map;
    // A shared link's viewport applies before the label layers stream in, so a deep-linked user isn't
    // parked on the city-wide view while they load (#4696).
    self.hasUrlViewport = MapSidebarUrlSync.applyUrlViewport(map);
    map.on('move', spotlight.updateTail); // Keep the tail pinned to the dot through pans/zooms/eases.
    initLabelMapLocationSearch(map, params.mapboxApiKey);
    labelMapOverlay.show();
    // A deep-linked popup's metadata fetch races map init; if it already arrived, place the camera now
    // instead of leaving the city-wide view up while the label layer streams in.
    if (deepLinkLabelId && !spotlight.spotlightedLabelId()) spotlight.spotlight(deepLinkLabelId, true);
  };

  createPSMap(params).then((m) => {
    self.map = m[0];
    self.mapData = m[4];
    // A ?regionId= link lands framed on that region, as it already does on the user dashboard's map —
    // unless the URL carries an explicit viewport, which wins (#4696).
    if (!self.hasUrlViewport) setRegionFocus(self.map);
    // Counts are view-scoped, matching the view-scoped downloads below: the viewport loader only holds a
    // padded region of the view, not the whole city, so counting everything loaded would report an
    // arbitrary area.
    const sidebarFilter = new MapSidebarFilter(self.map, self.mapData, {
      highQualityFilter: true,
      viewportCounts: true,
    });
    // Restore shared-link filters from the URL and keep the URL updated as filters and viewport change.
    new MapSidebarUrlSync(sidebarFilter, self.map);
    // "Download" control (#4095). Downloads follow the current view (GIS "export what you see", #5002): the
    // same bbox narrows the file that the view narrows the on-screen count by, so the two agree. Page-level
    // params the download can't carry (regions, routes, AI validation) leave the file wider than the count,
    // caveat included — bbox supersedes regionId at the endpoint, so a region link can't narrow the file.
    self.map.addControl(new MapDownloadControl({
      getFilterState: () => sidebarFilter.getState(),
      getVisibleLabelCount: () => sidebarFilter.getVisibleLabelCount(),
      getBbox: () => {
        const b = self.map.getBounds();
        return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].map((v) => v.toFixed(5)).join(',');
      },
      showsPartialFilterCaveat: Boolean(regionsParam) || Boolean(routesParam)
        || Boolean(aiValidationOptionsParam),
    }), 'top-right');
    // Popup arrows step through spatially nearby labels using the already-loaded map data, skipping any the
    // sidebar filters hide (#5124). Built after MapSidebarUrlSync above has restored the URL's filters: that
    // restore applies state without firing onChange, so it must land before anything derives arrow state.
    self.nearbyNav = createNearbyLabelNavigator(self.mapData, {
      isCandidate: (type, feature) => Boolean(sidebarFilter.matchesFilters(type, feature)),
    });
    params.popupLabelViewer.setNearbyNavigator(self.nearbyNav);
    // A user-driven filter change starts a fresh tour and re-derives the arrows' enabled states.
    sidebarFilter.onChange(() => self.nearbyNav.filtersChanged());

    // Viewport label loading (#5002): label data streams in (and refreshes as the map moves) after the map
    // is ready, so everything reading the loaded label set re-syncs on each data event.
    const labelLoader = self.mapData.labelLoader;
    const statusPill = new MapStatusPill(document.getElementById('labelmap-choropleth'), {
      suppressLoading: () => labelMapOverlay.isVisible(),
    });
    let zoomHintLogged = false;
    labelLoader.onData(() => {
      sidebarFilter.refresh();
      // Also re-evaluates the popup's prev/next arrows, which subscribe to the navigator they page over.
      self.nearbyNav.refresh();
      // Re-assert the spotlight for an open (deep-linked) label: the layer filters survive a data swap, but
      // the label itself may only now be present in the source — and its exact coords refine an approximate
      // camera-coords placement.
      const openLabelId = spotlight.spotlightedLabelId() || deepLinkLabelId;
      if (openLabelId && labelModal.open) spotlight.spotlight(openLabelId, true);
      labelMapOverlay.hide();
    });
    labelLoader.onError((e) => {
      console.error('LabelMap label feed failed', e);
      labelMapOverlay.showError();
    });
    labelLoader.onStateChange((state) => {
      statusPill.setState(state);
      if (state === 'belowFloor' && !zoomHintLogged) {
        zoomHintLogged = true;
        window.logWebpageActivity?.('LabelMap_ZoomHintShown');
      }
    });
    // View-scoped counts also change when a pan stays inside already-fetched data (no data event fires), so
    // refresh on settled moves too.
    let countsRefreshTimer = null;
    self.map.on('moveend', () => {
      clearTimeout(countsRefreshTimer);
      countsRefreshTimer = setTimeout(() => sidebarFilter.refresh(), 400);
    });
  }).catch((e) => {
    console.error('LabelMap failed to load', e);
    labelMapOverlay.showError();
  });
  /** @type {any} */ (window).map = self;
});
